// src/features/notifications/components/ToastStack.jsx
import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertTitle, IconButton, Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useNotifications } from '../context/NotificationProvider';
import { uiText } from '../../../shared/constants/uiText';
import '../styles/notifications.css';

const SUBJECT_MAX_LENGTH = 96;

function truncate(text) {
  if (!text) return '';
  return text.length > SUBJECT_MAX_LENGTH
    ? `${text.slice(0, SUBJECT_MAX_LENGTH - 1)}…`
    : text;
}

const ToastItem = memo(function ToastItem({ toast, dismissToast, navigate }) {
  const openTicket = () => {
    const ticketId = toast.ticketRef?._id;
    if (toast.kind !== 'ticket' || !ticketId) return;
    dismissToast(toast.id);
    navigate(`/tickets/${ticketId}`);
  };

  const handleKeyDown = event => {
    if (toast.kind !== 'ticket') return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openTicket();
    }
  };

  return (
    <Slide direction="left" in mountOnEnter unmountOnExit>
      <Alert
        severity={toast.severity}
        variant="filled"
        className={toast.kind === 'ticket' ? 'noti-toast--ticket' : 'noti-toast--aggregate'}
        role={toast.kind === 'ticket' ? 'button' : undefined}
        tabIndex={toast.kind === 'ticket' ? 0 : undefined}
        onClick={openTicket}
        onKeyDown={handleKeyDown}
        action={(
          <IconButton
            size="small"
            color="inherit"
            aria-label="Dismiss notification"
            onClick={event => {
              event.stopPropagation();
              dismissToast(toast.id);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      >
        {toast.kind === 'error' ? (
          <>
            <AlertTitle>Notification settings</AlertTitle>
            <span>{toast.message}</span>
          </>
        ) : toast.kind === 'aggregate' ? (
          <AlertTitle>{toast.count} new tickets</AlertTitle>
        ) : (
          <>
            <AlertTitle>{`New ticket · ${toast.ticketNo}`}</AlertTitle>
            <span className="noti-toast-subject">{truncate(toast.subject)}</span>
          </>
        )}
      </Alert>
    </Slide>
  );
});

export default function ToastStack() {
  const { toasts, dismissToast } = useNotifications();
  const navigate = useNavigate();
  return (
    <div className="noti-toast-stack" role="region" aria-label={uiText('NEW_TICKET_NOTIFICATIONS')}>
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} dismissToast={dismissToast} navigate={navigate} />
      ))}
    </div>
  );
}
